import {RequestState} from "modules/enums";
import {createReducer} from "redux-act";
import {
	createLeague,
	createLeagueSuccess,
	createLeagueFailed,
	clearCurrenLeague,
	fetchMyLeagues,
	fetchMyLeaguesSuccess,
	fetchMyLeaguesFailed,
	clearMyLeaguesList,
	fetchLeaguesToJoin,
	fetchLeaguesToJoinSuccess,
	fetchLeaguesToJoinFailed,
	clearJoinLeaguesList,
	fetchLeagueById,
	fetchLeagueByIdSuccess,
	fetchLeagueByIdFailed,
	updateLeague,
	updateLeagueFailed,
	updateLeagueSuccess,
	clearCurrenLeagueError,
	deleteLeague,
	deleteLeagueSuccess,
	deleteLeagueFailed,
	leaveLeague,
	leaveLeagueSuccess,
	leaveLeagueFailed,
	fetchLeagueUsers,
	fetchLeagueUsersSuccess,
	ILeagueUser,
	fetchLeagueUsersFailed,
	clearLeagueUsers,
	removeLeagueUser,
	removeLeagueUserSuccess,
	removeLeagueUserFailed,
	joinToLeagueSuccess,
	joinToLeague,
	joinToLeagueFailed,
} from "modules/actions/leagues";
import {ILeague, IMyLeaguesResponse, IJoinToLeaguesResponse} from "modules/types/leagues";
import {fetchLeaderboardFailed} from "modules/actions/leaderboards";

interface ILeaguesStore {
	currentLeague: ILeague | null;
	leaguesList: IMyLeaguesResponse;
	leaguesToJoinList: IJoinToLeaguesResponse;
	currentLeagueError: string | null;
	requestState: RequestState;
	leagueUsers: {
		users: ILeagueUser[];
		nextPage: boolean;
	};
}

const defaultState: ILeaguesStore = {
	currentLeague: null,
	leaguesList: {
		leagues: [],
		nextPage: false,
	},
	leaguesToJoinList: {
		leagues: [],
		nextPage: false,
	},
	currentLeagueError: null,
	requestState: RequestState.IDLE,
	leagueUsers: {
		users: [],
		nextPage: false,
	},
};

export const leagues = createReducer({}, defaultState)
	.on(createLeague, (state, payload) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(createLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.Received,
	}))
	.on(createLeagueFailed, (state, payload) => ({
		...defaultState,
		currentLeagueError: payload.message,
		requestState: RequestState.Received,
	}))
	.on(clearCurrenLeague, (state) => ({
		...state,
		currentLeague: null,
		currentLeagueError: null,
	}))
	.on(clearCurrenLeagueError, (state) => ({
		...state,
		currentLeagueError: null,
	}));

leagues
	.on(updateLeague, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(updateLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.Received,
	}))
	.on(updateLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.Error,
	}));

leagues
	.on(fetchMyLeagues, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(fetchMyLeaguesSuccess, (state, payload) => ({
		...state,
		leaguesList: payload,
		requestState: RequestState.Received,
	}))
	.on(fetchMyLeaguesFailed, (state) => ({
		...defaultState,
		requestState: RequestState.Error,
	}))
	.on(clearMyLeaguesList, (state) => ({
		...state,
		leaguesList: {
			leagues: [],
			nextPage: false,
		},
		requestState: RequestState.IDLE,
	}));

leagues
	.on(fetchLeaguesToJoin, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(fetchLeaguesToJoinSuccess, (state, payload) => ({
		...state,
		leaguesToJoinList: payload,
		requestState: RequestState.Received,
	}))
	.on(fetchLeaguesToJoinFailed, (state) => ({
		...defaultState,
		requestState: RequestState.Error,
	}))
	.on(clearJoinLeaguesList, (state) => ({
		...state,
		leaguesList: {
			leagues: [],
			nextPage: false,
		},
		requestState: RequestState.IDLE,
	}));

leagues
	.on(fetchLeagueById, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(fetchLeagueByIdSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.Received,
	}))
	.on(fetchLeagueByIdFailed, (state) => ({
		...defaultState,
		requestState: RequestState.Error,
	}))
	.on(fetchLeaderboardFailed, (state) => ({
		...defaultState,
		requestState: RequestState.Error,
	}));

leagues
	.on(joinToLeague, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(joinToLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: payload,
		requestState: RequestState.Received,
	}))
	.on(joinToLeagueFailed, () => ({
		...defaultState,
		requestState: RequestState.Error,
	}));

leagues
	.on(deleteLeague, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(deleteLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: null,
		requestState: RequestState.Received,
	}))
	.on(deleteLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.Error,
	}));

leagues
	.on(leaveLeague, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(leaveLeagueSuccess, (state, payload) => ({
		...state,
		currentLeague: null,
		requestState: RequestState.Received,
	}))
	.on(leaveLeagueFailed, (state) => ({
		...state,
		requestState: RequestState.Error,
	}));

leagues
	.on(fetchLeagueUsers, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(fetchLeagueUsersSuccess, (state, {users, nextPage}) => ({
		...state,
		leagueUsers: {
			...state.leagueUsers,
			users,
			nextPage,
		},
		requestState: RequestState.Received,
	}))
	.on(fetchLeagueUsersFailed, (state) => ({
		...state,
		requestState: RequestState.Error,
	}))
	.on(clearLeagueUsers, (state) => ({
		...state,
		leagueUsers: {
			users: [],
			nextPage: false,
		},
	}));

leagues
	.on(removeLeagueUser, (state) => ({
		...state,
		requestState: RequestState.Requested,
	}))
	.on(removeLeagueUserSuccess, (state, {userId}) => ({
		...state,
		leagueUsers: {
			...state.leagueUsers,
			users: state.leagueUsers.users.filter((user) => user.id !== userId),
		},
		requestState: RequestState.Received,
	}))
	.on(removeLeagueUserFailed, (state) => ({
		...state,
		requestState: RequestState.Error,
	}));
